import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from 'react-leaflet';
import { makePinIcon, makeCourierIcon } from '../../lib/leafletSetup';

const OSM_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// Geoapify "dark-matter-yellow-roads": dark, modern basemap with yellow roads.
const GEOAPIFY_STYLE = 'dark-matter-yellow-roads';
const GEOAPIFY_ATTR = 'Powered by <a href="https://www.geoapify.com/" target="_blank" rel="noreferrer">Geoapify</a> | &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors';
const geoapifyUrl = (key) => `https://maps.geoapify.com/v1/tile/${GEOAPIFY_STYLE}/{z}/{x}/{y}@2x.png?apiKey=${key}`;

// La chiave Geoapify è gestita globalmente dal Gestore (DB) con fallback alla env di build.
// Viene recuperata una sola volta e messa in cache a livello di modulo.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
let _geoKeyCache; // undefined = non ancora recuperata; string ('' incluso) = risolta
let _geoKeyPromise;
const fetchGeoKey = () => {
  if (_geoKeyCache !== undefined) return Promise.resolve(_geoKeyCache);
  if (!_geoKeyPromise) {
    _geoKeyPromise = fetch(`${BACKEND_URL}/api/public/app-config`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => { _geoKeyCache = (d && d.geoapify_key) || process.env.REACT_APP_GEOAPIFY_KEY || ''; return _geoKeyCache; })
      .catch(() => { _geoKeyCache = process.env.REACT_APP_GEOAPIFY_KEY || ''; return _geoKeyCache; });
  }
  return _geoKeyPromise;
};

// Resolve the app's primary (orange) token to a concrete color for Leaflet vector layers.
const primaryColor = () => {
  try {
    const hsl = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim();
    return hsl ? `hsl(${hsl})` : 'hsl(20 90% 48%)';
  } catch (e) { return 'hsl(20 90% 48%)'; }
};

const Recenter = ({ lat, lng, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) map.setView([lat, lng], zoom || map.getZoom(), { animate: true });
  }, [lat, lng, zoom, map]);
  return null;
};

const ClickHandler = ({ onPick }) => {
  useMapEvents({
    click(e) { onPick && onPick(e.latlng.lat, e.latlng.lng); },
  });
  return null;
};

export const OsmTiles = () => {
  const [key, setKey] = useState(_geoKeyCache);
  useEffect(() => {
    let mounted = true;
    if (_geoKeyCache === undefined) fetchGeoKey().then((k) => { if (mounted) setKey(k); });
    return () => { mounted = false; };
  }, []);
  return key
    ? <TileLayer key="geo" url={geoapifyUrl(key)} attribution={GEOAPIFY_ATTR} maxZoom={20} detectRetina={false} />
    : <TileLayer key="osm" url={OSM_URL} attribution={OSM_ATTR} />;
};

/**
 * Interactive location picker: draggable pin + optional radius circle.
 * onChange(lat, lng) fires on drag end and map click.
 */
export const PickerMap = ({ center, pin, radiusM, valid = true, onChange, className, testId = 'delivery-map', interactive = true, showRadius = true }) => {
  const color = useMemo(primaryColor, []);
  const c = center || pin || { lat: 45.4642, lng: 9.19 };
  const pinIcon = useMemo(() => makePinIcon(valid ? color : 'hsl(0 72% 50%)'), [color, valid]);
  const circleColor = valid ? color : 'hsl(0 72% 50%)';
  return (
    <div className={className} data-testid={testId}>
      <MapContainer center={[c.lat, c.lng]} zoom={14} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
        <OsmTiles />
        {interactive ? <ClickHandler onPick={onChange} /> : null}
        {pin && showRadius && radiusM ? (
          <Circle center={[pin.lat, pin.lng]} radius={radiusM}
            pathOptions={{ color: circleColor, weight: 2, fillColor: circleColor, fillOpacity: 0.12 }} />
        ) : null}
        {pin ? (
          <Marker
            position={[pin.lat, pin.lng]}
            icon={pinIcon}
            draggable={interactive}
            eventHandlers={interactive ? { dragend: (e) => { const ll = e.target.getLatLng(); onChange && onChange(ll.lat, ll.lng); } } : undefined}
          />
        ) : null}
        {pin ? <Recenter lat={pin.lat} lng={pin.lng} /> : null}
      </MapContainer>
    </div>
  );
};

/** Read-only tracking map: restaurant + customer pins + live courier marker. */
export const TrackMap = ({ restaurant, customer, courier, radiusM, className, testId = 'order-tracking-map' }) => {
  const color = useMemo(primaryColor, []);
  const restIcon = useMemo(() => makePinIcon('hsl(215 16% 47%)', 32), []);
  const custIcon = useMemo(() => makePinIcon(color, 38), [color]);
  const courierIcon = useMemo(() => makeCourierIcon(color), [color]);
  const focus = courier || customer || restaurant || { lat: 45.4642, lng: 9.19 };
  return (
    <div className={className} data-testid={testId}>
      <MapContainer center={[focus.lat, focus.lng]} zoom={14} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
        <OsmTiles />
        {restaurant && radiusM ? (
          <Circle center={[restaurant.lat, restaurant.lng]} radius={radiusM}
            pathOptions={{ color, weight: 1, fillColor: color, fillOpacity: 0.06 }} />
        ) : null}
        {restaurant ? <Marker position={[restaurant.lat, restaurant.lng]} icon={restIcon} /> : null}
        {customer ? <Marker position={[customer.lat, customer.lng]} icon={custIcon} /> : null}
        {courier ? <Marker position={[courier.lat, courier.lng]} icon={courierIcon} /> : null}
        {courier ? <Recenter lat={courier.lat} lng={courier.lng} /> : null}
      </MapContainer>
    </div>
  );
};
